import {
  Scale,
  Gavel,
  FileSignature,
  ShieldCheck,
  HandCoins,
  Laptop,
  Briefcase,
  Mic,
  PenLine,
  GraduationCap,
  BookOpenText,
  Building2,
  type LucideIcon,
} from "lucide-react";

export type Service = {
  slug: string;
  title: string;
  short: string;
  icon: LucideIcon;
  intro: string;
  details: string;
  whoFor: string[];
  offerings: string[];
  process: { title: string; body: string }[];
  scope: string;
  cta?: { label: string; to: string };
};

// Capabilities that apply to every service we offer, shown on the services
// index and on each service detail page.
export const UNIVERSAL_OFFERINGS = [
  "Provide legal information and legal advice on non-reserved matters",
  "Prepare legal opinions",
];

const COMMON_SCOPE =
  "This service is delivered as legal support / paralegal assistance. Across all our services we provide legal information and legal advice on non-reserved matters, and we prepare legal opinions. Nkadimeng Danny is not an admitted attorney; reserved work — court appearance, litigation on your behalf, conveyancing and notarial acts — falls outside our scope and we will refer you to an admitted legal practitioner where a matter requires it.";

export const services: Service[] = [
  {
    slug: "labour-relations",
    title: "Labour Relations",
    short:
      "Disciplinary hearings, grievances, workplace disputes and policy support for employees and employers.",
    icon: Scale,
    intro:
      "We support employees, employers and HR teams through the full spectrum of South African labour relations matters — from day-to-day workplace issues to formal disputes.",
    details:
      "South African workplaces are governed primarily by the Labour Relations Act (LRA), the Basic Conditions of Employment Act (BCEA), the Employment Equity Act (EEA) and the Code of Good Practice on Dismissal. Our role is to help you understand the procedure that applies to your situation, prepare the right paperwork, brief witnesses, and approach hearings or disputes with structure and confidence. We work with both employees who feel something unfair has happened, and employers who want to handle a matter correctly the first time.",
    whoFor: [
      "Employees facing disciplinary action, suspension or possible dismissal",
      "Employers and HR teams needing a fair, defensible internal process",
      "Small businesses without an in-house HR or legal function",
      "Managers preparing for grievance, incapacity or performance hearings",
    ],
    offerings: [
      "Disciplinary hearing preparation — charge sheets, evidence packs and witness prep (we do not chair or represent at the hearing)",
      "Grievance handling and internal investigation support",
      "Incapacity (ill-health and poor performance) process design",
      "Drafting and review of workplace policies and codes of conduct",
      "Retrenchment consultation (s189) preparation and document packs",
      "Pre-CCMA strategy and internal-appeal support",
    ],
    process: [
      {
        title: "1. Intake & document review",
        body: "We collect the charge sheet, employment contract, policies, correspondence and any witness notes.",
      },
      {
        title: "2. Procedure & strategy",
        body: "We map the correct procedure under the LRA / Code of Good Practice and agree the approach with you.",
      },
      {
        title: "3. Preparation",
        body: "We prepare bundles, opening statements, questions, witness statements and your evidence-in-chief.",
      },
      {
        title: "4. Hearing readiness",
        body: "We prepare you and your witnesses for the internal hearing. We do not represent employees or employers at the hearing itself — that role stays with you or an admitted representative.",
      },
    ],
    scope: COMMON_SCOPE,
  },
  {
    slug: "ccma-support",
    title: "CCMA Case Preparation",
    short:
      "Behind-the-scenes case preparation for conciliation, arbitration and con-arb matters — we do not represent you at the CCMA.",
    icon: Gavel,
    intro:
      "End-to-end CCMA preparation — we help you build a strong case, file correctly, and walk into conciliation or arbitration ready. We do not appear or represent you at the CCMA.",
    details:
      "Most dismissal disputes must be referred to the CCMA within 30 days of dismissal (90 days for unfair labour practices). Strict timelines, jurisdictional points and procedural rules apply. We help individuals, small employers and HR teams prepare LRA 7.11 referrals, build proper case files, prepare witnesses, and rehearse for conciliation or arbitration. We do not represent clients at the CCMA, bargaining councils or in court — where representation is required, we refer to an admitted attorney.",
    whoFor: [
      "Employees who have been dismissed, suspended or treated unfairly",
      "Small employers facing a CCMA referral",
      "HR practitioners who want a second pair of hands on a case",
      "Anyone unsure whether their matter qualifies as an unfair dismissal or unfair labour practice",
    ],
    offerings: [
      "Referral drafting and filing (LRA 7.11 / 7.12)",
      "Jurisdiction, condonation and rescission applications",
      "Case strategy, bundle and timeline preparation",
      "Witness preparation and signed statements",
      "Pre-hearing coaching and mock conciliation / arbitration",
      "Settlement negotiation, drafting and review",
    ],
    process: [
      {
        title: "1. Eligibility & timeline check",
        body: "We confirm jurisdiction, the dispute type and whether a condonation application is needed.",
      },
      {
        title: "2. Referral & filing",
        body: "We prepare and submit the correct LRA form and serve on the other party.",
      },
      {
        title: "3. Bundle & witness prep",
        body: "We assemble a chronological bundle, draft opening points, and walk witnesses through their evidence.",
      },
      {
        title: "4. Hearing readiness",
        body: "We coach you for conciliation or arbitration and refer to an admitted attorney where representation is required.",
      },
    ],
    scope: COMMON_SCOPE,
  },
  {
    slug: "legal-drafting",
    title: "Legal Drafting",
    short:
      "Professional drafting of contracts, affidavits, policies, agreements and formal letters.",
    icon: FileSignature,
    intro:
      "Clear, enforceable and compliant legal documents tailored to your situation — drafted with attention to detail and South African law.",
    details:
      "A well-drafted document prevents disputes, sets clear expectations and protects your interests. We draft and review documents in plain, precise language aligned with South African common law and the relevant statutes (Consumer Protection Act, POPIA, NCA, Companies Act and others). Where a document must be commissioned, notarised or registered, we explain the requirement and connect you with an appropriate official.",
    whoFor: [
      "Small businesses formalising client, supplier or employment relationships",
      "Landlords, tenants, buyers and sellers needing solid agreements",
      "Individuals who need an affidavit, letter of demand or formal notice",
      "Organisations rolling out internal policies and procedures",
    ],
    offerings: [
      "Commercial and service contracts",
      "Affidavits and sworn statements (to be commissioned)",
      "Internal policies and procedures (HR, IT, POPIA, code of conduct)",
      "Lease, sale, loan and supply agreements",
      "Letters of demand and formal notices",
      "Memoranda of Understanding and partnership agreements",
    ],
    process: [
      {
        title: "1. Brief",
        body: "We meet (in person or virtually) to understand the parties, intent, risks and any deadlines.",
      },
      {
        title: "2. First draft",
        body: "We deliver a clean first draft with explanatory notes on the key clauses.",
      },
      {
        title: "3. Revisions",
        body: "We refine through one or two rounds of comments until the document fits the deal.",
      },
      {
        title: "4. Signing pack",
        body: "We supply a signature-ready PDF/Word version with execution instructions.",
      },
    ],
    scope: COMMON_SCOPE,
  },
  {
    slug: "compliance",
    title: "Compliance Services",
    short:
      "Regulatory compliance, risk assessments and policy development for small businesses and organisations.",
    icon: ShieldCheck,
    intro:
      "Stay ahead of South African regulatory requirements with practical compliance advisory designed for SMEs and growing organisations.",
    details:
      "Compliance is not just about avoiding penalties — done well, it builds trust with clients, partners and regulators. We help organisations build compliance programmes that are proportionate to their size and risk profile, focusing on POPIA, employment compliance, sector-specific obligations and basic governance. We work alongside your management team and, where regulated advice is required, alongside the relevant professional (e.g. auditor, attorney, tax practitioner).",
    whoFor: [
      "SMEs that need a defensible baseline of policies",
      "Organisations preparing for an audit, tender or partnership due-diligence",
      "Companies starting to process personal information at scale (POPIA)",
      "Boards and managers wanting clearer risk and governance",
    ],
    offerings: [
      "POPIA readiness assessments and Information Officer support",
      "Employment compliance reviews (contracts, policies, BCEA basics)",
      "Sector and regulatory compliance gap analyses",
      "Risk registers and basic governance frameworks",
      "Policy development, rollout and staff training",
      "Annual compliance check-ins",
    ],
    process: [
      {
        title: "1. Discovery",
        body: "We map your business activities, data flows, contracts and current controls.",
      },
      {
        title: "2. Gap analysis",
        body: "We compare your current state against applicable laws and produce a prioritised gap list.",
      },
      {
        title: "3. Remediation plan",
        body: "We agree a realistic plan with owners, deadlines and quick wins.",
      },
      {
        title: "4. Implementation & training",
        body: "We help draft the policies, train staff and set up the next review cycle.",
      },
    ],
    scope: COMMON_SCOPE,
  },
  {
    slug: "debt-assistance",
    title: "Consumer Rights & Debt Support",
    short:
      "Debt-negotiation assistance, structured payment proposals and consumer-rights guidance. This is support and guidance only — not regulated debt review or debt counselling.",
    icon: HandCoins,
    intro:
      "If you are being pursued by creditors or struggling to keep up with repayments, we help you understand your rights and negotiate a workable way forward. We do not provide regulated debt review or debt-counselling services.",
    details:
      "South African consumers are protected by the National Credit Act (NCA) and the Consumer Protection Act (CPA). Many people don't realise how much room there is to negotiate, dispute incorrect amounts, or formalise affordable repayment arrangements. We help you understand the letter, summons or call you received, respond appropriately, and engage your creditors in writing. Where formal debt review or sequestration is required, we refer you to a registered debt counsellor or insolvency practitioner.",
    whoFor: [
      "Individuals receiving letters of demand or summonses",
      "Consumers being harassed by collection agencies",
      "People who want to negotiate a realistic repayment plan",
      "Anyone unsure whether a debt has prescribed or is correctly calculated",
    ],
    offerings: [
      "Review of letters of demand, summonses and statements",
      "Negotiation with creditors and collection agencies",
      "Structured payment arrangement proposals and confirmations",
      "Consumer protection guidance under the NCA and CPA",
      "Pre-litigation settlement support",
      "Referral to registered debt counsellors where appropriate",
    ],
    process: [
      {
        title: "1. Situation review",
        body: "We list every creditor, balance, document and deadline so you have one clear picture.",
      },
      {
        title: "2. Strategy",
        body: "We identify which debts to dispute, which to negotiate and which to settle first.",
      },
      {
        title: "3. Engagement",
        body: "We draft and send written communications to creditors on your behalf.",
      },
      {
        title: "4. Arrangement",
        body: "We document agreed arrangements so they are enforceable and so collections stop.",
      },
    ],
    scope: COMMON_SCOPE,
  },
  {
    slug: "court-online",
    title: "Court Online Support & Training",
    short:
      "Filing guidance, Court Online training and digital litigation support for legal professionals and litigants.",
    icon: Laptop,
    intro:
      "Practical, hands-on Court Online support — whether you need help with a single filing or full training for your team.",
    details:
      "The South African judiciary's Court Online and CaseLines platforms changed how matters are filed and managed. We help legal teams, paralegals and self-represented litigants register, file correctly, manage bundles, and serve documents electronically. Our training is practical: real screens, real workflows and real troubleshooting.",
    whoFor: [
      "Attorney firms onboarding new staff to Court Online / CaseLines",
      "Paralegals and assistants taking over filing responsibilities",
      "Self-represented litigants needing help with a specific filing",
      "Organisations who want a documented internal playbook",
    ],
    offerings: [
      "Court Online and CaseLines registration and onboarding",
      "Filing guidance and electronic service support",
      "Bundle indexing and pagination for digital litigation",
      "One-on-one and group training sessions (in person or virtual)",
      "Written playbooks and quick-reference cheat sheets",
      "Ongoing technical and procedural support",
    ],
    process: [
      {
        title: "1. Needs assessment",
        body: "We confirm the user's role, division and the workflows they touch most.",
      },
      {
        title: "2. Setup",
        body: "We help with registration, profile setup and access permissions.",
      },
      {
        title: "3. Training",
        body: "We walk through filing, bundles and service using real or sample matters.",
      },
      {
        title: "4. Support",
        body: "We stay on standby for the first filings so issues are resolved in real time.",
      },
    ],
    scope: COMMON_SCOPE,
  },
  {
    slug: "case-management",
    title: "Case Management",
    short:
      "End-to-end file management for your matter — intake, document control, deadlines, communications and a clear paper trail.",
    icon: Briefcase,
    intro:
      "We act as the central nervous system for your matter — keeping documents, deadlines, witnesses, parties and correspondence organised so nothing slips.",
    details:
      "Many cases are lost not on the merits but on missed dates, lost documents or unclear instructions. We run a structured case file for you: a single secure folder per matter, a chronological index of every document, a calendar of statutory and procedural deadlines, contact registers for parties and witnesses, and a running log of communications. We coordinate between you, your attorney (where one is involved), witnesses and other service providers. This is paralegal case management — we do not litigate, appear or sign court process in our own name.",
    whoFor: [
      "Individuals juggling multiple letters, dates and documents on one matter",
      "SMEs facing a dispute, CCMA referral or compliance investigation",
      "Attorneys outsourcing back-office file management on a brief",
      "Anyone who wants a calm, organised view of where their case stands",
    ],
    offerings: [
      "Matter intake, scoping and engagement letter",
      "Secure case file (chronological index, document register, bundle)",
      "Deadline diary and reminder system (statutory + procedural dates)",
      "Communication log, party register and witness contact sheet",
      "Drafting and dispatch of routine correspondence on instruction",
      "Weekly or fortnightly status reports in plain English",
      "Hand-over packs when a matter is referred to an admitted attorney",
    ],
    process: [
      {
        title: "1. Intake",
        body: "We take a full brief, list every document, party and date, and agree the scope in writing.",
      },
      {
        title: "2. Build the file",
        body: "We index existing material, open a secure folder, set up the deadline diary and the comms log.",
      },
      {
        title: "3. Run the matter",
        body: "We track every event, prompt you for action, draft routine correspondence and report regularly.",
      },
      {
        title: "4. Close or refer",
        body: "We close cleanly with a final pack — or hand over a litigation-ready bundle to your admitted attorney.",
      },
    ],
    scope: COMMON_SCOPE,
  },
  {
    slug: "public-speaking",
    title: "Public Speaking & Legal Education",
    short:
      "Talks, workshops and debate coaching on labour rights, POPIA, consumer law and access to justice — for schools, employers and community groups.",
    icon: Mic,
    intro:
      "Engaging, structured talks and workshops that make South African law understandable for the people it affects most.",
    details:
      "Legal knowledge changes lives — but only if it reaches people in language they can use. Drawing on years of debate, learner leadership and CCMA-floor experience, we deliver talks and workshops that translate the LRA, BCEA, POPIA, NCA and Consumer Protection Act into practical skills. Sessions are interactive, plain-language and grounded in real South African examples. We also coach school and tertiary debate teams on argument construction, evidence handling and delivery.",
    whoFor: [
      "Schools and universities — assemblies, civic-education days, debate teams",
      "Employers — staff inductions, manager training, POPIA awareness",
      "Trade unions, NGOs and community organisations",
      "Conferences, panels and youth leadership programmes",
    ],
    offerings: [
      "Keynote talks (30–60 min) on access to justice and legal empowerment",
      "Workplace rights workshops (LRA, BCEA, disciplinary process)",
      "POPIA & consumer protection sessions for staff and the public",
      "Debate coaching and adjudication for school and tertiary teams",
      "Career talks — the path from LLB to admission, and alternative legal careers",
      "Custom curriculum design with handouts and follow-up resources",
    ],
    process: [
      {
        title: "1. Brief",
        body: "We agree the audience, length, learning outcomes and any sensitivities.",
      },
      {
        title: "2. Tailor",
        body: "We adapt the material to the audience — examples, language level and case studies.",
      },
      {
        title: "3. Deliver",
        body: "We present in person or online with practical exercises, Q&A and take-home notes.",
      },
      {
        title: "4. Reinforce",
        body: "We share follow-up resources and an optional Q&A window for the days after the session.",
      },
    ],
    scope: COMMON_SCOPE,
  },
  {
    slug: "drafting-paralegal",
    title: "Drafting & Paralegal Services",
    short:
      "Legal research, document preparation, drafting support and administrative legal services.",
    icon: PenLine,
    intro:
      "Most legal problems are won or lost long before a courtroom. We handle the research, drafting, preparation and filing work that turns a stressful matter into a structured, defensible file.",
    details:
      "Our drafting and paralegal service is the practical backbone of everything we do. We research the law on your matter and explain it in plain English, draft the documents you need, prepare you for hearings, and lodge filings on Court Online and CaseLines. Every brief is checked against current South African law and POPIA-aligned before it leaves us. Where a matter calls for an admitted attorney or advocate, we say so up front and continue to support them in the background to keep your costs down.",
    whoFor: [
      "Individuals who need contracts, letters or affidavits drafted properly",
      "SMEs that need POPIA, employment or consumer-law compliance packs",
      "Employees preparing for CCMA, bargaining council or internal hearings",
      "Attorneys who want behind-the-scenes drafting and filing support",
    ],
    offerings: [
      "Research the law on your matter and explain it in plain English",
      "Draft contracts, leases, NDAs, letters of demand and formal correspondence",
      "Draft affidavits, statements and sworn declarations for commissioning",
      "Prepare CCMA referrals (LRA 7.11 / 7.12), bundles and witness statements",
      "Coach you and your witnesses for conciliation, arbitration or an internal hearing",
      "Build POPIA, employment and consumer-law compliance packs for SMEs",
      "Negotiate with creditors, debt collectors and counter-parties on your behalf",
      "Lodge and manage filings on Court Online and CaseLines",
      "Vet documents before you sign and flag risks early",
    ],
    process: [
      {
        title: "1. Intake & scoping",
        body: "We take a clear instruction, list the documents to be produced or filed, and agree a fixed scope and turnaround.",
      },
      {
        title: "2. Research & first draft",
        body: "We research the applicable South African law and prepare a first draft for your review.",
      },
      {
        title: "3. Revision & sign-off",
        body: "You comment, we revise. We flag risks, missing facts and clauses that need legal advice from an admitted practitioner.",
      },
      {
        title: "4. Filing or handover",
        body: "We file on Court Online / CaseLines where applicable, or hand over a clean pack ready to sign, commission or send.",
      },
    ],
    scope: COMMON_SCOPE,
  },
  {
    slug: "company-secretarial",
    title: "Company Secretarial Services",
    short:
      "CIPC filings, Beneficial Ownership, MOI drafting, statutory records and corporate governance support for South African companies.",
    icon: Building2,
    intro:
      "Reliable, professional company secretarial services that help businesses meet their statutory obligations and maintain good corporate governance under South African law.",
    details:
      "At Nkadimeng Danny Legal and Compliance Support, we work closely with startups, SMEs, non-profit organisations and established businesses to ensure statutory records remain accurate, compliance requirements are met, and corporate governance standards are maintained in accordance with the Companies Act 71 of 2008 and related legislation. We handle the administrative burden so directors and management can focus on running the business — from CIPC filings and Beneficial Ownership submissions to drafting Memoranda of Incorporation, convening meetings, and advising on directors' statutory duties.",
    whoFor: [
      "Startups and SMEs needing reliable CIPC and statutory compliance support",
      "Non-profit organisations maintaining governance and reporting standards",
      "Established companies updating directors, addresses or auditors",
      "Boards and directors seeking guidance on fiduciary duties and governance",
    ],
    offerings: [
      "Preparation and submission of Foreigner Assurance applications to the CIPC",
      "Preparation and filing of Beneficial Ownership information with the CIPC",
      "Drafting, reviewing and amendment of Memoranda of Incorporation (MOIs) tailored to your business",
      "Maintenance and updating of statutory company records",
      "Updating CIPC records: directors, auditors, registered addresses, company secretaries and other statutory information",
      "Preparation of annual and special resolutions",
      "Convening and administrative support for AGMs and other shareholder or board meetings",
      "Drafting accurate minutes of shareholder, board and committee meetings",
      "Advising on compliance obligations under the Companies Act and other applicable legislation",
      "Guidance on directors' statutory duties, fiduciary responsibilities and corporate governance best practices",
      "Monitoring legislative and regulatory developments relevant to your business",
      "General corporate governance and company secretarial support",
    ],
    process: [
      {
        title: "1. Compliance review",
        body: "We assess your current CIPC standing, statutory records and outstanding filings to establish a clear baseline.",
      },
      {
        title: "2. Remediation & filings",
        body: "We bring records up to date — Beneficial Ownership, director changes, MOI amendments and other CIPC submissions.",
      },
      {
        title: "3. Governance support",
        body: "We draft resolutions, minutes and meeting packs, and advise on directors' duties and statutory obligations.",
      },
      {
        title: "4. Ongoing maintenance",
        body: "We monitor legislative changes and keep your statutory records, registers and CIPC information current.",
      },
    ],
    scope: COMMON_SCOPE,
  },
];

export const getService = (slug: string) =>
  services.find((s) => s.slug === slug);
